import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import parse from 'html-react-parser';

import Portal from './Portal';
import { isModalVisible, getCurrentTarget } from '../../store/selectors';
import { doHideModal, doFetchSubscription } from '../../store/actions';
import { rem, colors, device } from '../../lib/global-styles';

const ModalWrapper = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  text-align: center;
  position: fixed;
  left: 0;
  width: 100%;
  height: 100vh;
  top: ${props => (props.isTarget ? 0 : '-100hv')};
  opacity: ${props => (props.isTarget ? 1 : 0)};
`;

const ModalDialog = styled.div`
  position: fixed;
  z-index: 1001;
  left: calc(50% - ${rem(195)});
  margin: ${rem(30)};

  @media ${device.desktop} {
    width: ${props => (props.width ? `${rem(props.width)}` : `${rem(580)}`)};
    left: calc(50% - ${props => (props.width ? `${rem(props.width / 2)}` : `${rem(300)}`)});
  }
`;

const ModalContent = styled.div`
  max-height: 85vh;
  padding-top: ${rem(20)};
  padding-bottom: 0;
  border-radius: ${rem(6)};
  background-clip: padding-box;
  background-color: ${colors.white};
  overflow-y: auto;
  letter-spacing: ${rem(0.23)};

  img {
    max-width: ${rem(148)};

    @media ${device.mobile} {
      margin-bottom: ${rem(10)};
    }
  }
`;

const Paragraph = styled.p`
  padding: ${rem(10)} ${rem(20)};
  font-size: ${rem(16)};
  color: ${colors.darkGrey};
  margin-bottom: ${rem(20)};
  line-height: ${rem(28)};

  span {
    font-weight: bold;
  }

  @media ${device.desktop} {
    padding: 0 ${rem(115)};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${rem(15)};
  right: ${rem(15)};
  width: ${rem(20)};
  height: ${rem(20)};
  border: 0;
  padding: 0;
  background-color: ${colors.grey};
  color: ${colors.darkGrey};
  font-size: ${rem(15)};
  border-radius: ${rem(15)};
`;

class Modal extends Component {
  state = {
    isHidden: true,
  };

  componentDidUpdate(prevProps) {
    const { isVisible } = this.props;

    if (prevProps.isVisible !== isVisible) {
      this.setState({ isHidden: !isVisible });
    }
  }

  onCloseButtonClick = () => {
    const { onHideModal } = this.props;

    onHideModal();
  };

  render() {
    const { isHidden } = this.state;
    const { isVisible, target, currentTarget, children, width, content } = this.props;

    return (
      <Portal>
        <ModalWrapper isTarget={isVisible && !isHidden && target === currentTarget}>
          <ModalDialog width={width}>
            <ModalContent>
              <CloseButton type="button" aria-label="Close modal" onClick={this.onCloseButtonClick}>
                &#x2715;
              </CloseButton>
              {content ? (
                <>
                  <img src={content.image} alt={content.title} />
                  <Paragraph>{parse(content.content)}</Paragraph>
                </>
              ) : null}
              {children}
            </ModalContent>
          </ModalDialog>
        </ModalWrapper>
      </Portal>
    );
  }
}

Modal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  currentTarget: PropTypes.string.isRequired,
  onHideModal: PropTypes.func.isRequired,
  target: PropTypes.string.isRequired,
  content: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onFetchSubscription: PropTypes.func,
};

const mapStateToProps = state => ({
  isVisible: isModalVisible(state),
  currentTarget: getCurrentTarget(state),
});

const mapDispatchToProps = dispatch => ({
  onHideModal: () => dispatch(doHideModal()),
  onFetchSubscription: () => dispatch(doFetchSubscription()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Modal);
